import { ArrowDownRight, ArrowUpRight, Minus } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { HIGHER_IS_BETTER } from '../utils/report-rates';

type ReportDeltaProps = {
  metricKey: string;
  /** Proportional change for counts, arithmetic difference of fractions for rates. Null means no prior period. */
  delta: number | null | undefined;
  days: number;
  asPoints?: boolean;
  /** Current rate fraction, used to choose decimal places on point deltas. */
  current?: number | null;
};

export function ReportDelta({
  metricKey,
  delta,
  days,
  asPoints = false,
  current,
}: ReportDeltaProps) {
  if (delta === null || delta === undefined) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums text-[#666]"
        title="No comparable data in the previous period"
      >
        — no prior period
      </span>
    );
  }

  const up = delta > 0.00005;
  const down = delta < -0.00005;
  const better = HIGHER_IS_BETTER[metricKey];
  const tone = !up && !down ? 'flat' : up === !!better ? 'good' : 'bad';
  const Icon = !up && !down ? Minus : up ? ArrowUpRight : ArrowDownRight;

  let word: string;
  if (asPoints) {
    const pts = Math.abs(delta) * 100;
    const ratePct = Math.abs(current ?? 0) * 100;
    const dp = ratePct < 1 || pts < 1 ? 2 : 1;
    if (pts < 0.005) {
      return (
        <span className="inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums text-[#666]">
          <Minus className="size-3" aria-hidden />
          no change
          <span className="font-normal text-neutral-500"> vs prev {days}d</span>
        </span>
      );
    }
    word = !up && !down ? 'no change' : `${pts.toFixed(dp)} pts`;
  } else {
    const pct = Math.abs(delta) * 100;
    const shown = Number(pct.toFixed(2));
    word = !up && !down ? 'no change' : `${shown}%`;
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums',
        tone === 'good' && 'text-[#006300]',
        tone === 'bad' && 'text-[#B3261E]',
        tone === 'flat' && 'text-[#666]'
      )}
    >
      <Icon className="size-3" aria-hidden />
      {word}
      <span className="font-normal text-neutral-500"> vs prev {days}d</span>
    </span>
  );
}
